import type { CalendarDate } from '../value-objects/CalendarDate';

/**
 * El periodo de vacaciones de una persona: de aniversario a aniversario.
 *
 * **Ésta es la regla de la empresa, y no es la que el saldo usaba.** Los días se
 * renuevan en cada aniversario laboral y se tienen hasta el día antes del
 * siguiente: quien entró un 16 de septiembre está gastando, hoy, los días que se
 * le concedieron el 16 de septiembre pasado, y el 15 de septiembre próximo es el
 * último día para usarlos. El saldo, en cambio, vivía por año calendario —un
 * documento por `${personId}_${añoCalendario}`, vencimiento contado desde el 31
 * de diciembre—, así que para todo el que no entró un 1 de enero el portal
 * medía un periodo que no existe.
 *
 * El resto del sistema ya trabajaba así. `completedYearsOfService` cuenta
 * aniversarios, `vacationVestsOn` es el primero, y el comentario del servidor lo
 * dice con todas sus letras: «anniversary to anniversary, never January to
 * December». Lo único que se había quedado en el calendario era el saldo.
 *
 * ---
 *
 * **Cuánto duran los días: «Vencimiento del saldo» meses desde el aniversario.**
 * Hasta el 17 sep 2026 morían en el aniversario siguiente —doce meses, fijos— y
 * el parámetro se mostraba y se editaba sin intervenir en nada: el perfil decía
 * «prescribe a los 18 meses» mientras el saldo vencía a los doce. Ahora manda.
 * Con dieciocho, los periodos se enciman seis meses, y en esa ventana una fecha
 * se puede pagar con dos saldos: se gasta primero el que vence antes. Ver
 * `allocateAcrossPeriods`.
 *
 * La deuda sí cruza, y no es una excepción a lo anterior: quien tomó más días de
 * los que tenía empieza el periodo siguiente debiendo, porque una deuda perdonada
 * por el paso del tiempo sería un premio por sobregirarse.
 */

export interface VacationPeriod {
  /**
   * El año del aniversario que abre el periodo, y la etiqueta con la que se
   * guarda el saldo.
   *
   * Para quien entró en septiembre de 2018, el periodo que corre hoy —agosto de
   * 2026— es el **2025**: el que abrió su aniversario del 16 de septiembre de
   * 2025. Es la diferencia que hacía que el saldo de esa persona estuviera
   * guardado bajo el año equivocado.
   */
  readonly year: number;
  /** El aniversario que lo abre. Inclusive. */
  readonly start: CalendarDate;
  /** El aniversario siguiente: el primer día que ya no pertenece al periodo. */
  readonly end: CalendarDate;
  /** El último día para gozarlos, que es lo que se le enseña a la gente. */
  readonly lastDay: CalendarDate;
  /**
   * Si el periodo es el primer año, todavía sin cumplir.
   *
   * El derecho existe desde el ingreso —`resolveEntitlement` nunca devuelve
   * menos que el mínimo de ley— pero no es tomable hasta el aniversario, que es
   * lo que ya decide `vacationEligibility`. Se marca aquí para que quien lea un
   * saldo sepa que ese periodo no vence: madura.
   */
  readonly isFirstYear: boolean;
}

/**
 * El periodo que contiene a `on`.
 *
 * Antes del primer aniversario devuelve el tramo desde el ingreso, que es el
 * único periodo que no empieza en un aniversario. `addYears` recorta el 29 de
 * febrero al 28, así que quien entró en bisiesto tiene aniversario todos los
 * años.
 */
export const periodFor = (hireDate: CalendarDate, on: CalendarDate): VacationPeriod => {
  const yearsToAnniversary = Math.max(0, on.year - hireDate.year);

  // Se busca desde el aniversario más cercano hacia atrás: contar años por
  // diferencia de calendario se equivoca cuando la fecha aún no llega.
  let start = hireDate.addYears(yearsToAnniversary);
  if (on.isBefore(start)) start = hireDate.addYears(Math.max(0, yearsToAnniversary - 1));

  const end = start.equals(hireDate) ? hireDate.addYears(1) : start.addYears(1);

  return {
    year: start.year,
    start,
    end,
    lastDay: end.addDays(-1),
    isFirstYear: start.equals(hireDate),
  };
};

/** La etiqueta del saldo vivo de una persona. */
export const periodYearOf = (hireDate: CalendarDate, on: CalendarDate): number =>
  periodFor(hireDate, on).year;

/** Un tramo de unas vacaciones dentro de un solo periodo. */
export interface PeriodSlice {
  readonly year: number;
  readonly from: CalendarDate;
  readonly to: CalendarDate;
}

/**
 * Los tramos en que un rango se parte por sus aniversarios.
 *
 * Unas vacaciones a caballo del aniversario pertenecen a **dos** periodos, y el
 * portal las cargaba enteras al de la fecha de inicio — el que está a punto de
 * caducar—. Esta es la mitad de cliente de `splitAcrossPeriods`, y como aquélla
 * no cuenta días: devuelve los cortes, y quien los use cuenta los hábiles con su
 * propio calendario de feriados.
 *
 * Duplicada del servidor por la misma razón que el resto de este archivo: las dos
 * mitades no comparten build. La que decide es la del servidor; ésta sólo evita
 * que el asistente avise de un saldo que no es el que se va a descontar.
 */
export const slicesByPeriod = (
  hireDate: CalendarDate,
  from: CalendarDate,
  to: CalendarDate,
): readonly PeriodSlice[] => {
  const slices: PeriodSlice[] = [];
  let cursor = from;

  /* Tope de vueltas y no una condición sola: un rango no cruza más de un puñado
     de aniversarios, y una parada rota no puede colgar el asistente. */
  for (let vuelta = 0; vuelta < 12 && !cursor.isAfter(to); vuelta += 1) {
    const period = periodFor(hireDate, cursor);
    const end = period.lastDay.isBefore(to) ? period.lastDay : to;

    slices.push({ year: period.year, from: cursor, to: end });
    cursor = end.addDays(1);
  }

  return slices;
};

/**
 * Cuántos días faltan para que el periodo se cierre.
 *
 * Negativo no ocurre para el periodo que contiene a `on`; se devuelve tal cual
 * para que quien lo reciba pueda decidir, en vez de recibir un cero que oculta
 * el caso.
 */
export const daysUntilPeriodEnds = (period: VacationPeriod, on: CalendarDate): number =>
  period.lastDay.epochDay - on.epochDay;

/**
 * Desde cuándo avisar de que el periodo se acaba.
 *
 * Sin ventana, la advertencia sale los 365 días del año para todo el que tenga
 * un día sin usar, y una advertencia permanente es decoración: se aprende a no
 * verla, y el mes que de verdad importa se pierde entre los once que no. Sesenta
 * días es lo que ya usa la tarea semanal `remindExpiringDays`, y conviene que
 * las dos hablen del mismo momento.
 */
export const EXPIRY_NOTICE_DAYS = 60;

export const isExpiringSoon = (
  period: VacationPeriod,
  on: CalendarDate,
  noticeDays: number = EXPIRY_NOTICE_DAYS,
): boolean => {
  if (period.isFirstYear) return false;
  const left = daysUntilPeriodEnds(period, on);
  return left >= 0 && left <= noticeDays;
};

// ---------------------------------------------------------------------------
// Periodos encimados: vencimiento y reparto
//
// **Mitad de cliente de `functions/src/vacation/periodSplit.ts`.** Las dos no
// comparten build; la que decide es la del servidor. Ésta existe para que el
// asistente reparta igual que la callable y no avise de un saldo que no es el que
// se va a descontar. Las dos se prueban contra la misma tabla de casos.
// ---------------------------------------------------------------------------

/**
 * Los límites de «Vencimiento del saldo».
 *
 * **Doce abajo**: con la ventana contada desde el aniversario, menos de doce meses
 * haría que los días vencieran antes de que abra el periodo siguiente, y la
 * persona pasaría meses sin nada que pedir. **Treinta y seis arriba**, lo que ya
 * ofrecía Configuración.
 */
export const EXPIRY_MONTHS_MIN = 12;
export const EXPIRY_MONTHS_MAX = 36;
export const EXPIRY_MONTHS_DEFAULT = 18;

/** Acota el parámetro. Un `6` guardado antes de que existiera el mínimo no deja a nadie sin días. */
export const clampExpiryMonths = (raw: number): number =>
  Number.isFinite(raw)
    ? Math.min(EXPIRY_MONTHS_MAX, Math.max(EXPIRY_MONTHS_MIN, Math.trunc(raw)))
    : EXPIRY_MONTHS_DEFAULT;

/**
 * El primer día en que los días de un periodo ya no sirven. Exclusivo.
 *
 * Aniversario más «Vencimiento del saldo» meses. Con el mínimo de doce, nunca
 * cae antes de que abra el periodo siguiente.
 */
export const validUntil = (period: VacationPeriod, expiryMonths: number): CalendarDate =>
  period.start.addMonths(clampExpiryMonths(expiryMonths));

/** El último día para gozarlos, que es lo que se le enseña a la gente. */
export const lastValidDay = (period: VacationPeriod, expiryMonths: number): CalendarDate =>
  validUntil(period, expiryMonths).addDays(-1);

/** El periodo que abre un aniversario dado. `year` es el del aniversario. */
const periodStartingIn = (hireDate: CalendarDate, year: number): VacationPeriod =>
  periodFor(hireDate, hireDate.addYears(Math.max(0, year - hireDate.year)));

/**
 * Los periodos cuyos días se pueden usar en algún día del rango, del más viejo al
 * más nuevo.
 *
 * **Sin el primer año**: sus días no se reservan antes de cumplirlo
 * —`vacationEligibility`—, y los primeros días usables viven en el periodo del
 * primer aniversario. Un periodo que madura no paga nada.
 */
export const livePeriodsBetween = (
  hireDate: CalendarDate,
  from: CalendarDate,
  to: CalendarDate,
  expiryMonths: number,
): readonly VacationPeriod[] => {
  const meses = clampExpiryMonths(expiryMonths);
  const primero = periodFor(hireDate, from).year - Math.ceil(meses / 12);
  const ultimo = periodFor(hireDate, to).year;
  const vivos: VacationPeriod[] = [];

  for (let year = Math.max(hireDate.year, primero); year <= ultimo; year += 1) {
    const period = periodStartingIn(hireDate, year);
    if (period.isFirstYear || period.year !== year) continue;
    if (period.start.isAfter(to)) continue;
    if (!validUntil(period, meses).isAfter(from)) continue;
    vivos.push(period);
  }
  return vivos;
};

/** Los periodos vivos en un día: los saldos que una pantalla tiene que enseñar hoy. */
export const livePeriodsOn = (
  hireDate: CalendarDate,
  on: CalendarDate,
  expiryMonths: number,
): readonly VacationPeriod[] => livePeriodsBetween(hireDate, on, on, expiryMonths);

/** Un periodo con lo que le queda, listo para repartir. */
export interface LivePeriodBalance {
  readonly year: number;
  readonly start: CalendarDate;
  /** Exclusivo. Ver `validUntil`. */
  readonly validUntil: CalendarDate;
  /** Lo que le queda antes de esta solicitud. Negativo es deuda: no paga nada. */
  readonly available: number;
}

export interface AllocatedShare {
  readonly year: number;
  readonly from: CalendarDate;
  readonly to: CalendarDate;
  readonly days: number;
}

export interface PeriodAllocation {
  readonly shares: readonly AllocatedShare[];
  /** Días hábiles que ningún periodo vigente pudo cubrir. */
  readonly shortfall: number;
}

/**
 * Reparte los días hábiles de un rango **gastando primero los que vencen antes**.
 *
 * Día por día: un periodo viejo puede agotarse a media solicitud, y el resto cae
 * en el siguiente. Ver la mitad del servidor, que es la que decide.
 */
export const allocateAcrossPeriods = (
  from: CalendarDate,
  to: CalendarDate,
  periods: readonly LivePeriodBalance[],
  isBusinessDay: (day: CalendarDate) => boolean,
): PeriodAllocation => {
  const restante = new Map(periods.map((p) => [p.year, Math.max(0, p.available)]));
  const porAnio = new Map<number, { from: CalendarDate; to: CalendarDate; days: number }>();
  const ordenados = [...periods].sort((a, b) => a.year - b.year);
  let shortfall = 0;

  let dia = from;
  for (let vuelta = 0; vuelta < 1000 && !dia.isAfter(to); vuelta += 1) {
    if (isBusinessDay(dia)) {
      const actual = dia;
      const paga = ordenados.find(
        (p) =>
          !p.start.isAfter(actual) && actual.isBefore(p.validUntil) && (restante.get(p.year) ?? 0) > 0,
      );
      if (paga === undefined) {
        shortfall += 1;
      } else {
        restante.set(paga.year, (restante.get(paga.year) ?? 0) - 1);
        const tramo = porAnio.get(paga.year);
        porAnio.set(
          paga.year,
          tramo === undefined ? { from: actual, to: actual, days: 1 } : { ...tramo, to: actual, days: tramo.days + 1 },
        );
      }
    }
    dia = dia.addDays(1);
  }

  const shares = [...porAnio.entries()]
    .sort(([a], [b]) => a - b)
    .map(([year, tramo]) => ({ year, ...tramo }));
  return { shares, shortfall };
};

/** Días naturales hasta el último día vigente. Negativo si ya venció. */
export const daysUntilExpiry = (period: VacationPeriod, on: CalendarDate, expiryMonths: number): number =>
  lastValidDay(period, expiryMonths).epochDay - on.epochDay;
